// src/shared/utils/currency.ts
//
// Single-source-of-truth currency resolution + money formatting for the whole
// SPA. EU instruments (XETRA `.DE`, Vienna `.VI`/`.VIE`) are already priced in
// EUR at the data source, so the ONLY thing that ever needs to change is the
// display glyph / ISO code. Every page/table/chart that renders money must go
// through `formatMoney` / `formatCompactMoney` (or `currencyGlyph` for chart
// axes) so no widget can silently regress back to a hardcoded `$`.
//
// Resolution priority (most authoritative first):
//   0. ETF-only NAV currency (`info.nav_currency`) — the fund's books are the
//      final authority for an ETF deep-dive page, so it dominates when passed.
//   1. Explicit backend-provided `currency` (e.g. `quote.currency`).
//   2. Exchange mapping (XETRA -> EUR, LSE -> GBP, WSE -> PLN, ...).
//   3. Symbol suffix mapping (`.DE` -> EUR, `.L` -> GBP, `.WA` -> PLN, ...).
//   4. Market-group hint (Top Performers labels rows `US` | `EU`).
//   5. Default `USD`.

// The full set of ISO-4217 codes this SPA can label. This is a superset of the
// venues we currently list in the Global Search Screener "Any exchange" filter
// plus common Yahoo-style suffixes, so onboarding a new venue is usually just a
// map entry (no type change). `formatMoney` / `formatCompactMoney` render any
// of these via `Intl.NumberFormat` automatically.
export type CurrencyCode =
    | 'USD'
    | 'EUR'
    | 'GBP'
    // GBX (Pence Sterling) is the LSE listing unit: 1 GBX = 0.01 GBP. It is not
    // an Intl-recognised ISO code, so every formatter below intercepts it and
    // renders it as "GBp".
    | 'GBX'
    | 'PLN'
    | 'CAD'
    | 'CHF'
    | 'SEK'
    | 'NOK'
    | 'DKK'
    | 'ISK'
    | 'CZK'
    | 'HUF'
    | 'TRY'
    | 'RUB'
    | 'JPY'
    | 'HKD'
    | 'CNY'
    | 'KRW'
    | 'TWD'
    | 'INR'
    | 'THB'
    | 'IDR'
    | 'MYR'
    | 'SGD'
    | 'PHP'
    | 'VND'
    | 'AUD'
    | 'NZD'
    | 'ILS'
    | 'SAR'
    | 'AED'
    | 'QAR'
    | 'KWD'
    | 'EGP'
    | 'ZAR'
    | 'BRL'
    | 'ARS'
    | 'CLP'
    | 'COP'
    | 'PEN'
    | 'MXN';

export interface CurrencyContext {
    /** Backend-provided ISO code — trusted first when present. */
    explicitCurrency?: string | null;
    /**
     * ETF-only: the fund's Net Asset Value currency (`info.nav_currency`).
     * When present it DOMINATES every other signal so the whole ETF page
     * renders in the currency the fund actually reports its books in.
     */
    navCurrency?: string | null;
    /** Ticker, used for the suffix fallback (`.DE`, `.L`, `.WA`, ...). */
    symbol?: string | null;
    /** Exchange short-name (e.g. `XETRA`, `LSE`, `WSE`, `NASDAQ`). */
    exchange?: string | null;
    /** Coarse market group used by the Top Performers leaderboard. */
    market?: 'US' | 'EU' | string | null;
}

// Set of every code we can confidently label, used to validate an explicit /
// NAV currency before trusting it (so a junk backend value falls through to
// the exchange/suffix heuristics instead of being echoed verbatim).
const KNOWN_CURRENCIES: ReadonlySet<string> = new Set<CurrencyCode>([
    'USD', 'EUR', 'GBP', 'GBX', 'PLN', 'CAD', 'CHF', 'SEK', 'NOK', 'DKK',
    'ISK', 'CZK', 'HUF', 'TRY', 'RUB', 'JPY', 'HKD', 'CNY', 'KRW', 'TWD',
    'INR', 'THB', 'IDR', 'MYR', 'SGD', 'PHP', 'VND', 'AUD', 'NZD', 'ILS',
    'SAR', 'AED', 'QAR', 'KWD', 'EGP', 'ZAR', 'BRL', 'ARS', 'CLP', 'COP',
    'PEN', 'MXN',
]);

// Exchange short-name -> listing currency. Keys are UPPER-CASE. Codes follow
// FMP's `exchangeShortName` vocabulary (the source behind the screener's
// "Any exchange" list); common Yahoo aliases (XLON, LON) are included as a
// safeguard. Extend this map to onboard a new venue — every view inherits it.
const EXCHANGE_TO_CURRENCY: Record<string, CurrencyCode> = {
    // --- North America (USD / CAD / MXN) ---
    NASDAQ: 'USD', NYSE: 'USD', AMEX: 'USD', NYSEARCA: 'USD', ARCA: 'USD',
    BATS: 'USD', CBOE: 'USD', OTC: 'USD', PNK: 'USD', OTCBB: 'USD',
    TSX: 'CAD', TSXV: 'CAD', TOR: 'CAD', NEO: 'CAD', CNQ: 'CAD',
    MEX: 'MXN', BMV: 'MXN',
    // --- United Kingdom (GBX - Pence Sterling; 1 GBX = 0.01 GBP) ---
    LSE: 'GBX', XLON: 'GBX', LON: 'GBX', LONDON: 'GBX', AQS: 'GBX',
    // --- Euro-zone venues (EUR) ---
    XETRA: 'EUR', ETR: 'EUR', FSX: 'EUR', FWB: 'EUR', GER: 'EUR', BER: 'EUR',
    STU: 'EUR', MUN: 'EUR', HAM: 'EUR', DUS: 'EUR', HAN: 'EUR',
    EURONEXT: 'EUR', PAR: 'EUR', AMS: 'EUR', BRU: 'EUR', LIS: 'EUR',
    MIL: 'EUR', BIT: 'EUR', MCE: 'EUR', BME: 'EUR', MAD: 'EUR',
    VIE: 'EUR', VIENNA: 'EUR', HEL: 'EUR', ATH: 'EUR', DUB: 'EUR', ISE: 'EUR',
    RIS: 'EUR', TAL: 'EUR', VSE: 'EUR',
    // --- Rest of Europe (non-EUR) ---
    OSL: 'NOK', OSE: 'NOK',
    STO: 'SEK',
    CPH: 'DKK',
    ICE: 'ISK',
    SIX: 'CHF', SWX: 'CHF', EBS: 'CHF', VTX: 'CHF',
    WSE: 'PLN', WAR: 'PLN', WARSAW: 'PLN',
    PRA: 'CZK', PRG: 'CZK',
    BUD: 'HUF',
    IST: 'TRY',
    MCX: 'RUB', MOEX: 'RUB',
    // --- Asia-Pacific ---
    JPX: 'JPY', TSE: 'JPY', TYO: 'JPY', JAS: 'JPY', OSA: 'JPY',
    HKSE: 'HKD', HKG: 'HKD',
    SHH: 'CNY', SHZ: 'CNY', SSE: 'CNY', SZSE: 'CNY', SHG: 'CNY',
    KSC: 'KRW', KOE: 'KRW', KRX: 'KRW', KOSDAQ: 'KRW',
    TAI: 'TWD', TWO: 'TWD', TWSE: 'TWD', TPE: 'TWD',
    BSE: 'INR', NSE: 'INR',
    SET: 'THB',
    IDX: 'IDR', JKT: 'IDR',
    KLS: 'MYR', KLSE: 'MYR', MYX: 'MYR',
    SES: 'SGD', SGX: 'SGD',
    PSE: 'PHP',
    HOSE: 'VND', HSX: 'VND', HNX: 'VND',
    ASX: 'AUD',
    NZE: 'NZD', NZX: 'NZD',
    // --- Middle East & Africa ---
    TLV: 'ILS',
    SAU: 'SAR', TADAWUL: 'SAR',
    DFM: 'AED', ADX: 'AED',
    QSE: 'QAR', DOH: 'QAR',
    KUW: 'KWD',
    EGX: 'EGP', CAI: 'EGP',
    JNB: 'ZAR', JSE: 'ZAR',
    // --- Latin America ---
    SAO: 'BRL', BVMF: 'BRL', BVSP: 'BRL',
    BUE: 'ARS', BCBA: 'ARS',
    SGO: 'CLP', SANTIAGO: 'CLP',
    BVC: 'COP',
    LIM: 'PEN',
};

// Yahoo-style ticker suffix -> listing currency (must include the leading dot
// so short suffixes like `.L` never false-match a plain ticker such as `AAPL`).
const SUFFIX_TO_CURRENCY: Record<string, CurrencyCode> = {
    '.L': 'GBX',
    '.DE': 'EUR', '.F': 'EUR', '.BE': 'EUR', '.SG': 'EUR', '.MU': 'EUR',
    '.DU': 'EUR', '.HM': 'EUR', '.HA': 'EUR',
    '.PA': 'EUR', '.AS': 'EUR', '.BR': 'EUR', '.LS': 'EUR',
    '.MI': 'EUR', '.MC': 'EUR', '.VI': 'EUR', '.VIE': 'EUR',
    '.HE': 'EUR', '.AT': 'EUR', '.IR': 'EUR',
    '.OL': 'NOK', '.ST': 'SEK', '.CO': 'DKK', '.IC': 'ISK',
    '.SW': 'CHF', '.VX': 'CHF',
    '.WA': 'PLN', '.PR': 'CZK', '.BD': 'HUF', '.IS': 'TRY', '.ME': 'RUB',
    '.T': 'JPY', '.HK': 'HKD', '.SS': 'CNY', '.SZ': 'CNY',
    '.KS': 'KRW', '.KQ': 'KRW', '.TW': 'TWD', '.TWO': 'TWD',
    '.NS': 'INR', '.BO': 'INR', '.BK': 'THB', '.JK': 'IDR', '.KL': 'MYR',
    '.SI': 'SGD', '.AX': 'AUD', '.NZ': 'NZD', '.TA': 'ILS', '.SR': 'SAR',
    '.SA': 'BRL', '.BA': 'ARS', '.SN': 'CLP', '.MX': 'MXN', '.JO': 'ZAR',
    '.TO': 'CAD', '.V': 'CAD', '.CN': 'CAD', '.NE': 'CAD',
};

/**
 * Resolve the canonical ISO currency code for a money-bearing value.
 * Never throws; always returns a concrete `CurrencyCode` (defaults `USD`).
 */
export function resolveCurrency(ctx: CurrencyContext = {}): CurrencyCode {
    // Venue currency (exchange, then symbol suffix) resolved up front so the
    // explicit-currency branch can honour the UK pence convention.
    let venue: CurrencyCode | null = null;
    const ex = (ctx.exchange || '').toUpperCase();
    if (ex in EXCHANGE_TO_CURRENCY) venue = EXCHANGE_TO_CURRENCY[ex];
    if (venue === null) {
        const sym = (ctx.symbol || '').toUpperCase();
        if (sym) {
            const match = Object.keys(SUFFIX_TO_CURRENCY).find((sfx) =>
                sym.endsWith(sfx),
            );
            if (match) venue = SUFFIX_TO_CURRENCY[match];
        }
    }

    // 0) ETF-only: the fund's NAV currency dominates everything else. FMP is
    //    CASE-SENSITIVE about sterling ("GBp" == pence/GBX, "GBP" == pounds),
    //    so honour that exact casing before falling back to the known-code set.
    const rawNav = (ctx.navCurrency || '').trim();
    if (rawNav) {
        if (rawNav === 'GBp') return 'GBX';
        const nav = rawNav.toUpperCase();
        if (KNOWN_CURRENCIES.has(nav)) return nav as CurrencyCode;
    }

    // 1) Trust an explicit backend-provided currency, honouring FMP's exact
    //    case-sensitivity. FMP returns EXACTLY "GBp" for the pence unit (GBX)
    //    and "GBP" for genuine pounds. So "GBp" -> GBX keeps an LSE pence
    //    price aligned with its scale ("GBX 1525" = £15.25, not "£1,525"),
    //    while a real "GBP" is TRUSTED as pounds -- this is what makes a
    //    pound-quoted LSE ETF like VUKE.L resolve to GBP instead of being
    //    force-labelled pence. A NON-sterling explicit code (EUR/USD/...)
    //    still overrides the venue; a garbage code falls through below.
    const rawExplicit = (ctx.explicitCurrency || '').trim();
    if (rawExplicit) {
        if (rawExplicit === 'GBp') return 'GBX';
        const explicit = rawExplicit.toUpperCase();
        if (KNOWN_CURRENCIES.has(explicit)) return explicit as CurrencyCode;
    }

    // 2) Venue mapping (exchange / symbol suffix).
    if (venue !== null) return venue;

    // 3) Market-group hint (Top Performers labels EU rows).
    const m = (ctx.market || '').toUpperCase();
    if (m === 'EU' || m === 'DE') return 'EUR';

    // 4) Default.
    return 'USD';
}

/**
 * The bare glyph for the resolved currency — handy for compact chart axes.
 * Derived from `Intl.NumberFormat` so every ISO code renders its correct
 * symbol (£, zł, C$, kr, ¥, ...) without a hand-maintained glyph table.
 */
export function currencyGlyph(ctx: CurrencyContext = {}): string {
    const code = resolveCurrency(ctx);
    // GBX has no Intl glyph. The browser would auto-render "GBp" for the code,
    // but we intercept it to show the explicit "GBX" label the product wants.
    if (code === 'GBX') return 'GBX';
    try {
        const parts = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            maximumFractionDigits: 0,
        }).formatToParts(0);
        return parts.find((p) => p.type === 'currency')?.value ?? code;
    } catch {
        return code;
    }
}

interface MoneyFormatOptions {
    /** Placeholder returned for null/NaN values. Defaults to `—`. */
    fallback?: string;
    /** Override the max fraction digits (e.g. 6 for sub-dollar crypto). */
    maximumFractionDigits?: number;
    /** Override the min fraction digits. */
    minimumFractionDigits?: number;
}

/**
 * Format a value as full currency using the resolved currency glyph.
 * e.g. `formatMoney(1234.5, { market: 'EU' }) -> "€1,234.50"`.
 */
export function formatMoney(
    value?: number | null,
    ctx: CurrencyContext = {},
    options: MoneyFormatOptions = {},
): string {
    const fallback = options.fallback ?? '—';
    if (value == null || !Number.isFinite(value)) return fallback;
    const currency = resolveCurrency(ctx);
    // GBX is not an Intl ISO code and would auto-render as "GBp" -> intercept it
    // and prefix the explicit "GBX" label (LSE prices stay in pence).
    if (currency === 'GBX') {
        return `GBX ${value.toLocaleString('en-US', {
            minimumFractionDigits: options.minimumFractionDigits ?? 2,
            maximumFractionDigits: options.maximumFractionDigits ?? 2,
        })}`;
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: options.minimumFractionDigits ?? 2,
        maximumFractionDigits: options.maximumFractionDigits ?? 2,
    }).format(value);
}

/**
 * Format a value as compact currency (B/M/K) using the resolved glyph.
 * e.g. `formatCompactMoney(3.66e12, { symbol: 'AAPL' }) -> "$3.66T"`.
 */
export function formatCompactMoney(
    value?: number | null,
    ctx: CurrencyContext = {},
    options: MoneyFormatOptions = {},
): string {
    const fallback = options.fallback ?? '—';
    if (value == null || !Number.isFinite(value)) return fallback;
    const currency = resolveCurrency(ctx);
    // GBX is not an Intl ISO code and would auto-render as "GBp" -> intercept it
    // and prefix the explicit "GBX" label.
    if (currency === 'GBX') {
        return `GBX ${value.toLocaleString('en-US', {
            notation: 'compact',
            maximumFractionDigits: options.maximumFractionDigits ?? 2,
        })}`;
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: options.maximumFractionDigits ?? 2,
    }).format(value);
}

/**
 * Safely format a large currency AGGREGATE (Market Cap, AUM, ...).
 *
 * Unlike the raw intraday PRICE (which LSE quotes in GBX/pence), FMP already
 * reports these aggregates in whole POUNDS for a UK listing, so they must NOT
 * be divided by 100 -- doing so would show them 100x too small. We therefore
 * only relabel the ``GBX`` code as ``GBP`` (the value passes through unchanged);
 * every other currency is untouched. Uses compact notation (T/B/M) to preserve
 * the institutional KPI look, and never throws on an exotic code.
 */
export const formatMarketCap = (
    marketCap?: number | null,
    ccy: string = 'USD',
    fallback = 'N/A',
): string => {
    if (marketCap == null || !Number.isFinite(marketCap)) return fallback;
    const val = Number(marketCap) || 0;
    let code = (ccy || 'USD').toUpperCase();

    // Aggregates are already in whole pounds -> relabel GBX to GBP (no scaling).
    if (code === 'GBX') {
        code = 'GBP';
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(val);
    } catch {
        return `${code} ${val.toLocaleString('en-US', {
            notation: 'compact',
            maximumFractionDigits: 2,
        })}`;
    }
};
